import { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { EVENT_TYPES, EVENT_TYPE_GROUPS, searchEventTypes } from "@shared/eventTypes";
import { SheetModal } from "./SheetModal";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  selected: string[];
  onChange: (next: string[]) => void;
  max?: number;
  title?: string;
  /**
   * When provided, only event types in this set are shown (the rest have no
   * events for the current city/timeframe). Empty/undefined → show everything.
   */
  availableTypes?: Set<string> | null;
  /** Event-type groups to hide entirely (by group slug), e.g. ["food"]. */
  excludeGroups?: string[];
}

export function EventTypeSelectModal({
  isOpen,
  onClose,
  selected,
  onChange,
  max,
  title = "What kind of event?",
  availableTypes,
  excludeGroups,
}: Props) {
  // Keep a type if it has events (in the set) or is already selected.
  const inCatalog = (t: { slug: string }) =>
    !availableTypes ||
    availableTypes.size === 0 ||
    availableTypes.has(t.slug) ||
    selected.includes(t.slug);
  const [draft, setDraft] = useState<string[]>(selected);
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setDraft(selected);
      setQuery("");
      setTimeout(() => searchRef.current?.focus(), 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const atLimit = max != null && draft.length >= max;

  const toggle = (slug: string) => {
    if (draft.includes(slug)) setDraft(draft.filter((s) => s !== slug));
    else if (!atLimit) setDraft([...draft, slug]);
  };

  const q = query.trim();
  const filtered = q
    ? searchEventTypes(q).filter(
        (t) => inCatalog(t) && !excludeGroups?.includes(t.group),
      )
    : null;

  return (
    <SheetModal isOpen={isOpen} onClose={onClose} className="sm:max-w-lg h-[88vh] sm:h-[80vh]">
      {/* Header */}
      <div className="px-5 pt-5 pb-4 flex items-center gap-3 border-b border-neutral-200 dark:border-neutral-800 shrink-0">
        <h2 className="text-lg font-semibold flex-1">{title}</h2>
        {max != null && (
          <span className="text-sm text-neutral-400 dark:text-neutral-500">
            {draft.length}/{max}
          </span>
        )}
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors text-neutral-500"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Search */}
      <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-800 shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
          <input
            ref={searchRef}
            type="text"
            placeholder="Search…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-9 pr-8 py-2 rounded-xl bg-neutral-100 dark:bg-neutral-800 border-0 outline-none text-sm placeholder:text-neutral-400 focus:ring-2 focus:ring-primary-500/40"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Chips */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
        {filtered ? (
          <TypeChips types={filtered} draft={draft} atLimit={atLimit} onToggle={toggle} emptyQuery={query} />
        ) : (
          EVENT_TYPE_GROUPS.map((g) => {
            if (excludeGroups?.includes(g.slug)) return null;
            const items = EVENT_TYPES.filter(
              (t) => t.group === g.slug && inCatalog(t),
            );
            if (items.length === 0) return null;
            return (
              <div key={g.slug}>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-500 mb-2.5">
                  {g.label}
                </p>
                <TypeChips types={items} draft={draft} atLimit={atLimit} onToggle={toggle} />
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div className="px-4 pb-5 pt-3 flex gap-2 border-t border-neutral-200 dark:border-neutral-800 shrink-0">
        {draft.length > 0 && (
          <button type="button" onClick={() => setDraft([])} className="btn btn-secondary">
            Clear
          </button>
        )}
        <button
          type="button"
          onClick={() => { onChange(draft); onClose(); }}
          className="btn btn-primary flex-1"
        >
          Done
        </button>
      </div>
    </SheetModal>
  );
}

function TypeChips({
  types,
  draft,
  atLimit,
  onToggle,
  emptyQuery,
}: {
  types: readonly { slug: string; emoji: string; label: string }[];
  draft: string[];
  atLimit: boolean;
  onToggle: (slug: string) => void;
  emptyQuery?: string;
}) {
  if (types.length === 0) {
    return (
      <p className="text-sm text-neutral-400 dark:text-neutral-500 py-4 text-center">
        No results for &ldquo;{emptyQuery}&rdquo;
      </p>
    );
  }
  return (
    <div className="flex flex-wrap gap-2">
      {types.map((t) => {
        const isOn = draft.includes(t.slug);
        const disabled = !isOn && atLimit;
        return (
          <button
            type="button"
            key={t.slug}
            disabled={disabled}
            onClick={() => onToggle(t.slug)}
            className={`
              inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[15px] font-semibold
              border transition-all select-none
              ${isOn
                ? "bg-black/10 dark:bg-white/[0.15] border-black/40 dark:border-white/60 text-base-content"
                : disabled
                  ? "bg-black/[0.03] dark:bg-white/[0.03] border-transparent text-base-content/30 cursor-not-allowed"
                  : "bg-black/[0.05] dark:bg-white/[0.05] border-transparent text-base-content/90 hover:border-black/20 dark:hover:border-white/25"
              }
            `}
          >
            <span className={`text-base leading-none ${disabled ? "opacity-30 grayscale" : ""}`}>{t.emoji}</span>
            <span>{t.label}</span>
          </button>
        );
      })}
    </div>
  );
}
